import { Fragment, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getBookApi, getNeighborsApi, getSpeciesDetailApi, listSourcePreviewBooksApi, trackEvent, type SpeciesDetail } from '../api';
import { renderSpeciesShareCard } from '../speciesShareCard';
import ExportPreview, { type ExportArtifact } from '../ExportPreview';
import ScientificText from '../ScientificText';
import { toast } from '../toast';
import { taxonomyParts } from '../lib/scientificText';
import { imageLink } from '../lib/imageUrl';
import { collectSpeciesSources, sourcePdfHref, type SpeciesSource } from '../lib/speciesSources';
import './Book.css';

interface NeighborGroup {
  title: string;
  hint: string;
  items: { id?: string; name: string; sub: string; icon: string; to?: string }[];
}

interface SpeciesView {
  id: string;
  name: string;
  category: string;
  region: string;
  cover: string;
  desc: string;
  groups: NeighborGroup[];
}

const PENDING_DESCRIPTION = '该品种的资料还在整理中，你可以先在智能问答中继续查询。';

function pendingView(id?: string, loading = false): SpeciesView {
  return {
    id: id ?? '',
    name: loading ? '正在加载品种资料…' : '品种资料待补充',
    category: '资料待补充',
    region: '资料待补充',
    cover: '🐟',
    desc: loading ? '正在读取该品种的资料…' : PENDING_DESCRIPTION,
    groups: [],
  };
}

// 图节点标签 → 展示图标
function iconForLabel(label: string): string {
  switch (label) {
    case 'Species': return '🐟';
    case 'Category': return '🏷';
    case 'Waterway': return '🌊';
    case 'Region': return '📍';
    case 'BookRef': return '📘';
    case 'ImageRef': return '🖼';
    case 'Chunk': return '📄';
    default: return '🔗';
  }
}
// 关系类型 → 分组标题 + 提示
const RELATION_GROUP: Record<string, { title: string; hint: string }> = {
  SIMILAR_TO:     { title: '🔗 相似品种', hint: '外形或分类相近' },
  BELONGS_TO:     { title: '🏷 分类归属', hint: '该品种所属分类' },
  LIVES_IN:       { title: '🌊 栖息水系', hint: '常见生活水域' },
  DISTRIBUTED_IN: { title: '📍 分布地域', hint: '主要分布区域' },
  MENTIONED_IN:   { title: '📘 相关资料', hint: '收录该品种的内容' },
  HAS_IMAGE:      { title: '🖼 图片资料', hint: '与该品种相关的图片' },
};

function nodeTypeLabel(label?: string): string {
  switch (label) {
    case 'Species': return '水产品种';
    case 'Category': return '品种分类';
    case 'Waterway': return '水系';
    case 'Region': return '地区';
    case 'BookRef': return '图书资料';
    case 'ImageRef': return '图片资料';
    case 'Chunk': return '资料摘录';
    default: return '相关资料';
  }
}

/**
 * 后端 `/api/v1/graph/neighbors/{id}` 返回 `{ nodes: GraphNode[], edges: GraphEdge[] }`，
 * 而非旧代码假设的 `{ center, neighbors }`——以前形状对不上，导致页面永远回退到 demo。
 * 这里据实把"中心节点 + 各条边的另一端"按 **关系类型(edge.label)** 归类展示。
 */
function textValue(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function truncateDescription(value: string): string {
  const normalized = value.replace(/\s+/g, ' ').trim();
  return normalized.length > 260 ? `${normalized.slice(0, 257)}…` : normalized;
}

function mergeApi(api: any, centerId: string | undefined): SpeciesView {
  const nodes: any[] = Array.isArray(api?.nodes) ? api.nodes : [];
  const edges: any[] = Array.isArray(api?.edges) ? api.edges : [];
  if (!nodes.length) return pendingView(centerId);

  const nodeById: Record<string, any> = {};
  for (const n of nodes) nodeById[n.id] = n;
  // 中心节点：优先按路由 id 命中，否则取第一个 Species 节点。
  const center = (centerId && nodeById[centerId]) || nodes.find((n) => n.label === 'Species');
  if (!center) return pendingView(centerId);
  const centerKey = center.id ?? centerId;

  const relatedNode = (edge: any) => {
    if (edge.from_id === centerKey) return nodeById[edge.to_id];
    if (edge.to_id === centerKey) return nodeById[edge.from_id];
    return undefined;
  };

  const location = edges
    .filter((edge) => edge.label === 'LIVES_IN' || edge.label === 'DISTRIBUTED_IN')
    .map(relatedNode)
    .map((node) => textValue(node?.name) ?? textValue(node?.properties?.name))
    .find(Boolean);
  const categoryNode = edges
    .filter((edge) => edge.label === 'BELONGS_TO')
    .map(relatedNode)
    .map((node) => textValue(node?.name) ?? textValue(node?.properties?.name))
    .find(Boolean);
  // 图谱中的 Chunk 仅在与中心节点直接相连时才可作为简介兜底，避免混入其它品种资料。
  const relatedSnippet = edges
    .map(relatedNode)
    .filter((node) => node?.label === 'Chunk')
    .map((node) => textValue(node?.properties?.snippet) ?? textValue(node?.properties?.text))
    .find(Boolean);

  // 按关系类型分组；每条边取"非中心"的一端作为邻居。
  const groupMap: Record<string, NeighborGroup> = {};
  for (const e of edges) {
    if (e.from_id !== centerKey && e.to_id !== centerKey) continue;
    const otherId = e.from_id === centerKey ? e.to_id : e.from_id;
    const nb = nodeById[otherId];
    if (!nb) continue;
    // Source records have a dedicated, deduplicated citation section below.
    if (nb.label === 'Chunk' || nb.label === 'BookRef') continue;
    const cfg = RELATION_GROUP[e.label] ?? { title: '🔗 相关内容', hint: '更多相关资料' };
    const g = groupMap[cfg.title] ?? (groupMap[cfg.title] = { title: cfg.title, hint: cfg.hint, items: [] });
    const props = nb.properties ?? {};
    // Images without a display name use their caption.
    let name: string = nb.name || props.name;
    if (!name) {
      if (nb.label === 'ImageRef') name = props.caption ?? '关联图片';
      else name = nb.label;
    }
    // 同一邻居可能因双向边重复出现（如 SIMILAR_TO 互指），按 id 去重。
    if (g.items.some((x) => x.id && x.id === nb.id)) continue;
    g.items.push({
      id: nb.id,
      name,
      sub: nb.label === 'Species' ? (props.category ?? '水产品种') : nodeTypeLabel(nb.label),
      icon: iconForLabel(nb.label),
      // 只有品种节点能继续下钻到品种详情；其它类型节点不跳转。
      to: nb.label === 'Species' && nb.id ? `/species/${nb.id}` : undefined,
    });
  }
  const groups = Object.values(groupMap);

  const cprops = center.properties ?? {};
  return {
    id: center.id ?? centerId ?? '',
    name: textValue(center.name) ?? textValue(cprops.name) ?? '品种资料待补充',
    category: textValue(cprops.category) ?? categoryNode ?? '资料待补充',
    region: textValue(cprops.region) ?? textValue(cprops.waterway) ?? location ?? '资料待补充',
    cover: iconForLabel(center.label ?? 'Species'),
    desc: truncateDescription(
      textValue(cprops.intro)
      ?? textValue(cprops.desc)
      ?? textValue(cprops.summary)
      ?? relatedSnippet
      ?? PENDING_DESCRIPTION,
    ),
    groups,
  };
}

export default function SpeciesDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [view, setView] = useState<SpeciesView>(() => pendingView(id, true));
  const [detail, setDetail] = useState<SpeciesDetail | null>(null);
  const [sources, setSources] = useState<SpeciesSource[]>([]);
  const [availableSourceBookIds, setAvailableSourceBookIds] = useState<string[]>([]);
  const [previewableBookIds, setPreviewableBookIds] = useState<string[]>([]);
  const [resolvedId, setResolvedId] = useState<string | null>(null);
  const [exportArtifact, setExportArtifact] = useState<ExportArtifact | null>(null);
  const [cardGenerating, setCardGenerating] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setView(pendingView(id, true));
    setDetail(null);
    setSources([]);
    setAvailableSourceBookIds([]);
    setPreviewableBookIds([]);
    setResolvedId(null);
    Promise.all([
      getNeighborsApi(id, 1).catch(() => null),
      getSpeciesDetailApi(id).catch(() => null),
      listSourcePreviewBooksApi().catch(() => [] as string[]),
    ]).then(([graph, rich, previewIds]) => {
      if (cancelled) return;
      const collected = collectSpeciesSources(graph, id, rich?.sources, rich?.books);
      const knownBooks = new Set([...previewIds, ...(rich?.books.map((book) => book.id) ?? [])]);
      setSources(collected);
      setAvailableSourceBookIds([...knownBooks]);
      // Legacy graph chunks can point at deleted books. Preserve their text,
      // but verify unknown book targets before making them navigable.
      const unknownBooks = [...new Set(collected.map((source) => source.bookId))].filter((bookId) => !knownBooks.has(bookId));
      Promise.all(unknownBooks.map(async (bookId) => {
        const book = await getBookApi(bookId).catch(() => null);
        return { bookId, book };
      })).then((results) => {
        if (cancelled) return;
        const found = results.filter((result) => result.book);
        setAvailableSourceBookIds([...knownBooks, ...found.map((result) => result.bookId)]);
        setSources(collected.map((source) => {
          const title = found.find((result) => result.bookId === source.bookId)?.book?.title;
          return source.bookTitle === '相关图书' && typeof title === 'string' && title.trim()
            ? { ...source, bookTitle: title.trim() } : source;
        }));
      });
      setPreviewableBookIds(previewIds);
      setResolvedId(graph || rich ? id : null);
      const graphView = graph ? mergeApi(graph, id) : pendingView(id);
      if (rich) {
        setDetail(rich);
        rich.books.forEach((book) => trackEvent('book_impression', {
          book_id: book.id,
          species_id: rich.id,
          path: `/species/${rich.id}`,
          source: 'species_detail',
        }));
        setView({
          ...graphView,
          id: rich.id,
          name: rich.name,
          category: rich.category ?? graphView.category,
          region: rich.catalog_number != null
            ? [rich.volume, `资源编号 ${rich.catalog_number}`, rich.source_page != null ? `PDF P.${rich.source_page}` : undefined].filter(Boolean).join(' · ')
            : rich.production_areas[0] ?? rich.regions[0] ?? rich.waterways[0] ?? graphView.region,
          cover: rich.cover_url ?? graphView.cover,
          desc: truncateDescription(rich.intro ?? graphView.desc),
        });
      } else {
        setView(graphView);
      }
    });
      return () => { cancelled = true; };
  }, [id]);

  async function downloadSpeciesCard() {
    if (cardGenerating) return;
    setCardGenerating(true);
    const speciesId = id ?? view.id;
    const filename = `鱼种资料_${view.name.replace(/[\\/:*?"<>|]/g, '_')}.png`;
    const source = detail?.sources.find((item) => item.page === detail.source_page) ?? detail?.sources[0];
    const photo = detail?.images.find((item) => item.url === detail.cover_url) ?? detail?.images[0];
    try {
      const result = await renderSpeciesShareCard({
        name: view.name,
        scientific: detail?.scientific,
        category: view.category,
        description: detail?.role || detail?.intro || view.desc,
        imageUrl: imageLink(photo?.url || detail?.cover_url || view.cover),
        imageCaption: photo?.caption,
        catalogNumber: detail?.catalog_number,
        volume: detail?.volume,
        page: source?.page ?? detail?.source_page,
        bookTitle: source?.book_title ?? detail?.books[0]?.title,
      });
      setExportArtifact({ url: result.url, filename, kind: 'species', imageMissing: !result.imageIncluded });
      trackEvent('share_created', { species_id: speciesId, path: `/species/${speciesId}`, metadata: { type: 'card' } });
    } catch (error) {
      toast(error instanceof Error ? error.message : '生成分享卡失败，请重试');
    } finally {
      setCardGenerating(false);
    }
  }

  const isCatalogResource = detail?.catalog_number != null;
  const sourceReference = detail?.sources.find((source) =>
    source.page != null
    && Number.isInteger(source.page)
    && source.page > 0
    && (detail.source_page == null || source.page === detail.source_page)
    && previewableBookIds.includes(source.book_id),
  );
  const similarSpecies = view.groups
    .find((group) => group.title.includes('相似品种'))
    ?.items
    .filter((item) => item.name && item.name !== view.name)
    .slice(0, 2) ?? [];
  const identifyQuestion = similarSpecies.length > 0
    ? `${view.name}和${similarSpecies.map((item) => item.name).join('、')}怎么区分？`
    : `${view.name}有哪些辨认要点？`;

  return (
    <div className="container">
      <div className="book-hero">
        <div className="cover-wrap">
          <div className="cover species-cover">
            <div className="icon">
              {/^https?:|^\//.test(view.cover)
                ? <img src={view.cover} alt={`${view.name} 图片`} />
                : view.cover}
            </div>
            <div className="cname">{view.name.slice(0, 6)}</div>
            <div className="cen">SPECIES · CN</div>
          </div>
        </div>
        <div className="meta">
          <span className="pill">🐠 水产品种资料</span>
          <h1>{view.name}</h1>
          <div className="sub">{view.category} · {view.region}</div>
          <p className="desc">{view.desc}</p>
          {resolvedId === id && <div className="actions">
            {sourceReference && <Link className="btn primary" to={`/source-page/${sourceReference.book_id}/${sourceReference.page}`} target="_blank" rel="noopener noreferrer" aria-label="打开原文 PDF（新窗口）">打开原文 PDF ↗</Link>}
            <Link className={`btn ${sourceReference ? 'dark' : 'primary'}`} to={`/chat?q=${encodeURIComponent(view.name + ' 的分布和习性？')}`}>💬 在问答中查询</Link>
            <Link className="btn accent" to={`/chat?q=${encodeURIComponent(identifyQuestion)}`} style={{ textDecoration: 'none' }}>
              🔍 {similarSpecies.length > 0 ? '对比相似品种' : '查看辨认要点'}
            </Link>
            <button className="btn dark" type="button" disabled={cardGenerating} aria-busy={cardGenerating} onClick={() => void downloadSpeciesCard()}>{cardGenerating ? '正在生成分享卡…' : '制作分享卡'}</button>
          </div>}
        </div>
      </div>

      {detail && (
        <>
          <div className="section-title"><h2>🧭 品种档案</h2><span className="hint">名称、分类、分布与养殖信息</span></div>
          <div className={`card species-facts ${isCatalogResource ? 'catalog-facts' : ''}`}>
            {isCatalogResource ? (
              <>
                <Fact label="目录编号" values={[`${detail.volume ?? ''} 第 ${detail.catalog_number} 号`, detail.source_page != null ? `PDF 物理页 ${detail.source_page}` : ''].filter(Boolean)} />
                <Fact label="学名" values={detail.scientific ? [detail.scientific] : []} format="scientific" />
                <Fact label="俗名" values={detail.aliases} />
                <Fact label="分类地位" values={detail.taxonomy ? [detail.taxonomy] : []} format="taxonomy" />
                <Fact label="地位作用" values={detail.role ? [detail.role] : []} />
                <Fact label="养殖分布" values={detail.distribution ? [detail.distribution] : []} />
                <Fact label="养殖模式" values={detail.farming_mode ? [detail.farming_mode] : []} />
                <Fact label="开发利用情况" values={detail.development ? [detail.development] : []} />
              </>
            ) : (
              <>
                <Fact label="学名" values={detail.scientific ? [detail.scientific] : []} format="scientific" />
                <Fact label="别名" values={detail.aliases} />
                <Fact label="产地" values={detail.production_areas} />
                <Fact label="地域" values={detail.regions} />
                <Fact label="水系" values={detail.waterways} />
                <Fact label="特征" values={detail.features} />
                <Fact label="习性" values={detail.habits} />
              </>
            )}
          </div>

          {detail.images.length > 0 && (
            <>
              <div className="section-title"><h2>🖼 原书图片</h2><span className="hint">完整展示 · 可打开原图查看细节</span></div>
              <div className="card species-images">
                {detail.images.map((image) => <SpeciesImage key={image.id} image={image} />)}
              </div>
            </>
          )}

          {detail.books.length > 0 && (
            <>
              <div className="section-title"><h2>📚 相关图书</h2><span className="hint">收录该品种的图书资料</span></div>
              <div className="card species-books">
                {detail.books.map((book) => (
                  <div className="species-book" key={book.id}>
                    <div><b>《{book.title}》</b><span>{book.authors.join(' / ') || '作者待补充'}</span></div>
                    <div className="actions">
                      <Link className="chip" to={`/book/${book.id}`} onClick={() => trackEvent('book_click', { book_id: book.id, species_id: detail.id, source: 'species_detail' })}>查看详情</Link>
                      {book.purchase_url && <a className="chip active" href={book.purchase_url} target="_blank" rel="noopener noreferrer" onClick={() => trackEvent('book_jump', { book_id: book.id, species_id: detail.id, source: 'species_detail' })}>前往购买</a>}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

        </>
      )}

      {sources.length > 0 && (
        <section aria-labelledby="species-sources-heading">
          <div className="section-title"><h2 id="species-sources-heading">资料来源</h2><span className="hint">按图书、章节与页码归并</span></div>
          <div className="card species-sources">
            {sources.map((source) => {
              const pdfHref = sourcePdfHref(source, previewableBookIds);
              const bookAvailable = availableSourceBookIds.includes(source.bookId);
              return (
                <article key={source.key}>
                  <div className="species-source-heading">
                    <div>
                      {bookAvailable ? <Link className="species-source-title" to={`/book/${encodeURIComponent(source.bookId)}`}>《{source.bookTitle}》</Link>
                        : <span className="species-source-title">{source.bookTitle === '相关图书' ? '历史资料摘录' : `《${source.bookTitle}》`}</span>}
                      <div className="species-source-location">{[source.chapter, source.page == null ? '页码待补充' : `${pdfHref ? 'PDF 物理页' : '页码'} ${source.page}`].filter(Boolean).join(' · ')}</div>
                    </div>
                    {pdfHref ? <Link className="source-page-link" to={pdfHref} target="_blank" rel="noopener noreferrer" aria-label={`打开原文 PDF：${source.bookTitle}，第${source.page}页（新窗口）`}>打开原文 PDF ↗</Link>
                      : bookAvailable ? <Link className="source-page-link" to={`/book/${encodeURIComponent(source.bookId)}`}>查看图书资料</Link>
                        : <span className="species-source-location">原图书暂不可用</span>}
                  </div>
                  {source.entryNames.length > 0 && <p className="species-source-entries">收录条目：{source.entryNames.join('、')}</p>}
                  {source.excerpts.length > 0 && <details><summary>查看资料摘录</summary>{source.excerpts.map((excerpt, index) => <p key={index}><ScientificText text={excerpt} names={detail?.scientific ? [detail.scientific] : []} /></p>)}</details>}
                </article>
              );
            })}
          </div>
        </section>
      )}

      {view.groups.map((g, gi) => (
        <div key={gi}>
          <div className="section-title"><h2>{g.title}</h2><span className="hint">{g.hint}</span></div>
          <div className="card species-mini">
            {g.items.map((it, i) => {
              const inner = (
                <>
                  <div className="ico">{it.icon}</div>
                  <div><b>{it.name}</b><div className="s">{it.sub}</div></div>
                </>
              );
              return it.to
                ? <Link key={i} className="m" to={it.to} style={{ textDecoration: 'none', color: 'inherit' }}>{inner}</Link>
                : <div key={i} className="m">{inner}</div>;
            })}
          </div>
        </div>
      ))}

      <div className="footer">©2026 上海渔联网科技 · 水产品种资料</div>
      <ExportPreview artifact={exportArtifact} onClose={() => setExportArtifact(null)} />
    </div>
  );
}

function Fact({ label, values, format }: { label: string; values: string[]; format?: 'scientific' | 'taxonomy' }) {
  const text = values.join(' / ');
  return <div><span>{label}</span><b>{!values.length ? '待补充'
    : format === 'scientific' ? <i className="scientific-name">{text}</i>
      : format === 'taxonomy' ? taxonomyParts(text).map((part, index) => part.italic
        ? <i key={index} className="scientific-name">{part.text}</i>
        : <Fragment key={index}>{part.text}</Fragment>) : text}</b></div>;
}

function SpeciesImage({ image }: { image: { url: string; caption?: string } }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [image.url]);
  return (
    <figure>
      {failed
        ? <div className="species-image-fallback">🖼️<span>图片暂不可用</span></div>
        : <img src={image.url} alt={image.caption ?? '品种图片'} loading="lazy" onError={() => setFailed(true)} />}
      <figcaption>
        <span>{image.caption ?? '品种图片'}</span>
        {!failed && imageLink(image.url) && <a href={imageLink(image.url)} target="_blank" rel="noopener noreferrer">查看原图 ↗</a>}
      </figcaption>
    </figure>
  );
}
